import { type StudySelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useGetMeiaKopTemplates, useUpdateMeiaStudy } from '@queries/meia.query';
import { type MeiaStudyKopFormValues, meiaStudyKopSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { type FC, useEffect, useMemo, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { STUDY_ADVERSARY_PROFILE_OPTIONS, STUDY_TIME_HORIZON_OPTIONS } from './meia-study-options.util';
import { MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaKopModalProps = {
  isOpen: boolean;
  onClose: () => void;
  study: StudySelectDto;
};

export const MeiaKopModal: FC<MeiaKopModalProps> = (props) => {
  const { isOpen, onClose, study } = props;
  const { data: kopTemplates } = useGetMeiaKopTemplates();
  const { isPending: isSaving, mutateAsync: updateStudy } = useUpdateMeiaStudy();
  const [isDiscardChangesOpen, setIsDiscardChangesOpen] = useState(false);

  const defaultValues: MeiaStudyKopFormValues = useMemo(
    () => ({
      adversaryProfiles: study.adversaryProfiles ?? [],
      keyOperationalProblem: study.keyOperationalProblem ?? '',
      timeHorizon: study.timeHorizon ?? null,
    }),
    [study.adversaryProfiles, study.keyOperationalProblem, study.timeHorizon],
  );

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validation = meiaStudyKopSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please fix the KOP value.' });
        return;
      }
      await updateStudy({
        adversaryProfiles: validation.data.adversaryProfiles,
        keyOperationalProblem: validation.data.keyOperationalProblem.trim()
          ? validation.data.keyOperationalProblem
          : null,
        studyId: study.id,
        timeHorizon: validation.data.timeHorizon,
      });
      successToast({ description: 'Key Operational Problem saved.' });
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<MeiaStudyKopFormValues>(),
    validators: { onSubmit: meiaStudyKopSchema },
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(defaultValues);
  }, [defaultValues, form, isOpen]);

  const resolvedTemplates = kopTemplates?.kopTemplates ?? [];

  const requestClose = (): void => {
    if (form.state.isDirty) {
      setIsDiscardChangesOpen(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Modal
        centered
        footer={{
          rightButton: {
            children: isSaving ? 'Saving...' : 'Save',
            disabled: isSaving,
            onClick: () => void form.handleSubmit(),
          },
        }}
        header={{
          actions: (
            <MeiaTemplatePopover
              onSelect={(template) =>
                form.setFieldValue('keyOperationalProblem', template.description ?? template.name)
              }
              templates={resolvedTemplates.map((template) => ({
                description: template.description,
                id: template.id,
                name: template.name,
              }))}
            />
          ),
          title: 'Key Operational Problem',
        }}
        isOpen={isOpen}
        onClose={requestClose}
        width="640px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <p className="text-sm text-gray-600">
            Anchor the study with a single problem statement. All missions hang under it.
          </p>

          <form.AppForm>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <form.AppField name="adversaryProfiles">
                {(field) => (
                  <field.SelectMultipleField
                    label="Adversary Profile"
                    options={STUDY_ADVERSARY_PROFILE_OPTIONS}
                    placeholder="Select adversary profiles"
                  />
                )}
              </form.AppField>
              <form.AppField name="timeHorizon">
                {(field) => (
                  <field.SelectField
                    clearable
                    label="Time Horizon"
                    options={STUDY_TIME_HORIZON_OPTIONS}
                    placeholder="Select time horizon"
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="keyOperationalProblem">
              {(field) => (
                <field.TextAreaField
                  placeholder="State the operational problem the study must solve. Frame it as an outcome, not a list of tasks."
                  label="Problem Statement"
                  rows={8}
                />
              )}
            </form.AppField>
          </form.AppForm>
        </form>
      </Modal>
      <ConfirmationModal
        cancelLabel="Keep Editing"
        confirmLabel="Discard Changes"
        description="Closing now will discard unsaved Key Operational Problem edits."
        isOpen={isDiscardChangesOpen}
        onConfirm={onClose}
        setIsOpen={setIsDiscardChangesOpen}
        title="Discard unsaved changes?"
      />
    </>
  );
};
